/**
 * UNDRGRND instant navigation (Swup) — keeps the global audio bar alive across
 * page navigations so playback never stops.
 *
 * The audio player (#ug-audio-player) is rendered OUTSIDE the swapped container
 * (.main-content) in theme.liquid, so Swup never touches it and its <audio>
 * keeps playing. After each swap we re-run the theme's per-page init so legacy
 * jQuery behaviours (gallery, sliders, responsive "positions") and the audio
 * modules are re-attached to the freshly injected DOM.
 *
 * Swup + plugins are self-hosted as theme assets (ug-vendor-swup*.js) and expose
 * globals: Swup, SwupHeadPlugin, SwupScriptsPlugin, SwupBodyClassPlugin.
 *
 * Gated behind window.UG_SWUP_ENABLED so it can be toggled from theme settings.
 * v2 — app-proxy (/apps/) URLs excluded from interception (Fileflare downloads fix).
 */
(function () {
  'use strict';

  if (!window.UG_SWUP_ENABLED) return;
  if (window.__ugSwupBooted) return;
  // Never boot instant navigation on app-proxy pages (Fileflare download
  // pages etc.) — they are functional pages where interception risks
  // breaking file links, and instant-nav adds nothing there.
  if (/^\/apps\//.test(window.location.pathname)) return;
  window.__ugSwupBooted = true;

  // URLs that should always do a full page load (different layout / no .main-content).
  // /apps/ covers Shopify app proxies — most importantly the Fileflare download
  // pages and their /apps/digital-downloads/download/<id> links, which return
  // file redirects, not HTML. Intercepting those made Swup fetch the ZIP and
  // render the storefront 404 ("Container missing in incoming document").
  // The file-extension guard protects any other direct file links. The Preload
  // plugin consults this too (via swup.shouldIgnoreVisit), so hovering a
  // download link no longer fires a real download request.
  function shouldIgnore(url) {
    return (
      /\/(checkout|account|cart\b|admin)/.test(url) ||
      /\/cart$/.test(url) ||
      /\/apps\//.test(url) ||
      /\.(zip|mp3|wav|aiff?|pdf|midi?)(\?|$)/i.test(url)
    );
  }

  // The Judge.me review widget is a client-rendered ES module that only executes
  // on a full page load (the server HTML ships an EMPTY <div id=judgeme_product_reviews>).
  // After an instant-nav swap the container arrives empty and never repopulates,
  // leaving an empty bordered "black box". Re-inject the revamp widget module with
  // a unique query so the browser evaluates a fresh instance that renders into the
  // new container. (ES modules only evaluate once per URL, hence the cache-bust.)
  function ugRerenderJudgeMeReviews() {
    try {
      var w = document.getElementById('judgeme_product_reviews');
      if (!w || w.children.length > 0) return; // missing, or already populated
      var base = window.jdgm && window.jdgm.CDN_BASE_URL;
      var entry = w.getAttribute('data-entry-point');
      if (!base || !entry) return;

      // The widget's review data is delivered as an inline script
      // (jdgm.data.reviewWidget[id] = {...}) which the opt-in ScriptsPlugin does
      // NOT re-run after a swap — so a freshly navigated product has no data and
      // the module renders nothing. Re-execute those inline data scripts by
      // replacing each node with a fresh clone (preserves any CSP nonce/type).
      document.querySelectorAll('script:not([src])').forEach(function (orig) {
        if (!orig.textContent || orig.textContent.indexOf('jdgm.data.reviewWidget') === -1) return;
        var clone = document.createElement('script');
        for (var i = 0; i < orig.attributes.length; i++) {
          clone.setAttribute(orig.attributes[i].name, orig.attributes[i].value);
        }
        clone.textContent = orig.textContent;
        orig.parentNode.replaceChild(clone, orig);
      });

      document.querySelectorAll('script.jdgm-ug-rerender').forEach(function (el) {
        el.remove();
      });
      var s = document.createElement('script');
      s.type = 'module';
      s.src = base + entry + (base.indexOf('?') > -1 ? '&' : '?') + 'ugswup=' + Date.now();
      s.className = 'jdgm-ug-rerender';
      document.head.appendChild(s);
    } catch (e) {}
  }

  // Close any header menu left open from the previous page. The header lives
  // OUTSIDE the swapped .main-content container, so its open dropdown state
  // (desktop mega-menu + mobile menu) is never reset by the soft swap and would
  // otherwise stay open after navigating — forcing a manual close.
  function ugCloseHeaderMenus() {
    if (!window.jQuery) return;
    var $ = window.jQuery;
    try {
      // Desktop mega-menu (see mainMenu() in shop.js)
      $('.main-header').removeClass('dropdown-open');
      $('.main-menu .dropdown').removeClass('active');
      var $panel = $('.main-menu-dropdown-panel .wrapper');
      $panel.stop(true, true).hide().css('height', 'auto');
      $('.main-menu-dropdown-panel .columns').remove();
      // Mobile menu (toggled open via .mobile-menu)
      $('.mobile-menu').hide();
    } catch (e) {}
  }

  // Lazy-load heavy theme modules on first instant-nav visit to catalog/product pages.
  var ugLazyScriptCache = {};

  function ugScriptAlreadyLoaded(key) {
    if (key === 'packIndex') return Array.isArray(window.UG_PACK_INDEX);
    if (key === 'productDemos') return typeof window.ugBootProductDemos === 'function';
    if (key === 'previewTracks') return typeof window.ugBootPreviewPanels === 'function';
    if (key === 'previewAudioMatch') return !!document.querySelector('script[src*="ug-preview-audio-match"]');
    if (key === 'bundleProduct') return typeof window.ugBootBundleProduct === 'function';
    if (key === 'pdpExtras') return !!document.querySelector('script[src*="ug-product-pdp-extras"]');
    if (key === 'storefrontExtras') return !!document.querySelector('script[src*="ug-storefront-extras"]');
    return false;
  }

  function ugLoadScriptOnce(url, key, isModule) {
    if (!url || ugScriptAlreadyLoaded(key)) {
      return Promise.resolve();
    }
    if (ugLazyScriptCache[key]) {
      return ugLazyScriptCache[key];
    }
    ugLazyScriptCache[key] = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      if (isModule) s.type = 'module';
      s.src = url;
      s.onload = function () { resolve(); };
      s.onerror = reject;
      document.body.appendChild(s);
    });
    return ugLazyScriptCache[key];
  }

  function ugNeedsProductDemos() {
    var body = document.body;
    return (
      body.classList.contains('template-collection') ||
      body.classList.contains('template-search') ||
      body.classList.contains('template-index') ||
      body.classList.contains('template-product') ||
      !!document.querySelector('[data-ug-product-demo-handle]')
    );
  }

  function ugEnsureLazyScripts() {
    var urls = window.UG_LAZY_SCRIPT_URLS;
    if (!urls) return Promise.resolve();

    var chain = Promise.resolve();

    if (ugNeedsProductDemos()) {
      chain = chain.then(function () {
        return ugLoadScriptOnce(urls.productDemos, 'productDemos');
      });
    }

    if (
      document.body.classList.contains('template-collection') ||
      document.body.classList.contains('template-cart') ||
      document.body.classList.contains('template-product')
    ) {
      chain = chain.then(function () {
        return ugLoadScriptOnce(urls.storefrontExtras, 'storefrontExtras');
      });
    }

    if (document.querySelector('[data-ug-pdp-extras]')) {
      chain = chain
        .then(function () { return ugLoadScriptOnce(urls.packIndex, 'packIndex'); })
        .then(function () { return ugLoadScriptOnce(urls.pdpExtras, 'pdpExtras'); });
    }

    if (document.body.classList.contains('template-product')) {
      chain = chain.then(function () { return ugLoadScriptOnce(urls.previewTracks, 'previewTracks'); });
    }

    if (
      document.body.classList.contains('template-product-bundle') ||
      document.querySelector('[data-ug-bundle-product]')
    ) {
      chain = chain
        .then(function () { return ugLoadScriptOnce(urls.previewAudioMatch, 'previewAudioMatch'); })
        .then(function () { return ugLoadScriptOnce(urls.bundleProduct, 'bundleProduct', true); });
    }

    return chain.catch(function () {});
  }

  /* Bundle sticky is reparented to <body>; Swup only swaps .main-content. */
  function ugPortStickyToBody(el) {
    if (el && el.parentNode !== document.body) {
      document.body.appendChild(el);
    }
  }
  window.ugPortStickyToBody = ugPortStickyToBody;

  function ugRemoveOrphanStickyBars() {
    document.querySelectorAll('[data-ug-bundle-sticky]').forEach(function (el) {
      el.remove();
    });
  }
  window.ugRemoveOrphanStickyBars = ugRemoveOrphanStickyBars;

  // Re-run everything that normally fires on a fresh page load.
  function ugReinit() {
    ugRemoveOrphanStickyBars();

    try {
      window.scrollTo(0, 0);
    } catch (e) {}

    ugCloseHeaderMenus();

    // Legacy theme per-page init (gallery, sliders, responsive positions, etc.).
    if (typeof window.ugThemePageInit === 'function') {
      try {
        window.ugThemePageInit();
      } catch (e) {}
    }
    // Product price / variant init (reads the fresh product_json from the swapped page).
    if (typeof window.ugInitProductVariant === 'function') {
      try {
        window.ugInitProductVariant();
      } catch (e) {}
    }
    // Audio + product modules (self-guard, so re-booting on new DOM is safe).
    ['ugBootProductDemos', 'ugBootPreviewPanels', 'ugBootBundleProduct'].forEach(function (fn) {
      if (typeof window[fn] === 'function') {
        try {
          window[fn]();
        } catch (e) {}
      }
    });

    // Third-party widgets already listen for these SPA-style events (Judge.me
    // dark-theme observer re-arms here, so it re-styles the freshly rendered widget).
    ['turbo:load', 'turbolinks:load', 'page:load'].forEach(function (ev) {
      try {
        document.dispatchEvent(new Event(ev));
      } catch (e) {}
    });

    // Re-render the Judge.me review widget (client-only, doesn't survive swaps).
    ugRerenderJudgeMeReviews();

    // Give the welcome popup a chance to fire now that the page changed — e.g.
    // the shopper just left a bundle page (where it's intentionally suppressed)
    // for an eligible page. The function self-gates on the cooldown/session flags.
    if (typeof window.ugMaybeShowWelcomePopup === 'function') {
      try { window.ugMaybeShowWelcomePopup(); } catch (e) {}
    }

    // Currency conversion (if the multi-currency switcher is active).
    try {
      if (
        typeof window.Currency !== 'undefined' &&
        window.jQuery &&
        window.jQuery('[name=currencies]').length
      ) {
        window.Currency.convertAll(window.shopCurrency, window.jQuery('[name=currencies]').val());
      }
    } catch (e) {}

    // Analytics page views (these do not auto-fire on AJAX navigation).
    try {
      var path = location.pathname + location.search;
      if (typeof window.ga === 'function') window.ga('send', 'pageview', path);
      if (typeof window.gtag === 'function') window.gtag('event', 'page_view', { page_path: path });
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push({ event: 'page_view', page: path });
      }
    } catch (e) {}
  }

  function start() {
    window.__ugSwupStage = 'start';

    var Swup = window.Swup;
    if (typeof Swup !== 'function') {
      window.__ugSwupStage = 'error';
      window.__ugSwupErr = 'Swup vendor global not found';
      return;
    }

    var plugins = [];
    if (typeof window.SwupHeadPlugin === 'function') {
      plugins.push(new window.SwupHeadPlugin({ persistAssets: true }));
    }
    if (typeof window.SwupScriptsPlugin === 'function') {
      // Opt-in: only re-run scripts explicitly marked [data-swup-reload-script]
      // (the product_json inline block). All other body scripts (shop.js, the
      // audio player, modules, vendors) load once; re-init is handled by ugReinit().
      plugins.push(new window.SwupScriptsPlugin({ optin: true }));
    }
    if (typeof window.SwupBodyClassPlugin === 'function') {
      plugins.push(new window.SwupBodyClassPlugin());
    }
    // Preload the next page's HTML on hover/focus so the click feels instant
    // (each product page is ~400KB / ~500ms to fetch). Hover-only by default —
    // we deliberately do NOT preload every visible link to avoid mass fetching.
    if (typeof window.SwupPreloadPlugin === 'function') {
      plugins.push(new window.SwupPreloadPlugin());
    }

    try {
      var swup = new Swup({
        containers: ['.main-content'],
        cache: true,
        // Subtle cross-page fade. Swup waits for the CSS transition on the
        // container (see the gated fade styles in theme.liquid) before/after swap.
        animationSelector: '.main-content',
        ignoreVisit: function (url) {
          return shouldIgnore(url);
        },
        plugins: plugins
      });

      window.ugSwup = swup;
      window.__ugSwupStage = 'ready';

      // page:view fires after the content is replaced on each navigation (it does
      // NOT fire on the initial page load — shop.js already initialised that).
      // Other page:view handlers (BodyClassPlugin updating <body class>,
      // ScriptsPlugin re-running the product_json block) may run after ours, so we
      // defer one frame to let the body class + DOM + product_json fully settle.
      // Otherwise PAGE.hasClass('template-product') can still be the previous
      // page's class and the product init (theme.variantSelected / gallery image)
      // gets skipped — especially on the fast cached/preloaded path.
      var raf =
        window.requestAnimationFrame ||
        function (cb) {
          return setTimeout(cb, 16);
        };
      // Close any open header dropdown the moment a navigation starts, so it
      // doesn't linger on screen during the cross-page fade.
      swup.hooks.on('visit:start', function () {
        ugCloseHeaderMenus();
        ugRemoveOrphanStickyBars();
      });
      swup.hooks.on('page:view', function () {
        ugEnsureLazyScripts();
        raf(function () {
          ugReinit();
          try {
            document.dispatchEvent(new CustomEvent('ug:page-view'));
          } catch (e) {}
        });
      });
    } catch (err) {
      window.__ugSwupStage = 'error';
      window.__ugSwupErr = (err && (err.stack || err.message)) || String(err);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
